import PasswordField from '@components/v2/form/PasswordField/PasswordField';
import TextField from '@components/v2/form/TextField/TextField';
import AuthPanel from '@components/v2/layout/AuthPanel/AuthPanel';
import Button from '@components/v2/ui/Button/Button';
import { Controller } from 'react-hook-form';
import useLoginForm, { LoginFormValues } from '../../../../components/LoginForm/useLoginForm';
import LangEnum from '../../../../lang/LangEnum';
import { LoginPageTestIds } from '../../../../utils/testIds/LoginPage.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import useOAuthPasswordPanelStyles from './oauthPasswordPanel.styles';

interface OAuthPasswordPanelProps {
  defaultEmail: string;
  onSubmit: (values: LoginFormValues) => Promise<void>;
  onBackToEmail: () => void;
}

const OAuthPasswordPanel = ({ defaultEmail, onSubmit, onBackToEmail }: OAuthPasswordPanelProps) => {
  const { classes } = useOAuthPasswordPanelStyles();
  const { translate } = useTranslation();
  const { formMethods, handleSubmit } = useLoginForm({ onSubmit, defaultEmail });
  const {
    control,
    formState: { isSubmitting },
  } = formMethods;

  return (
    <AuthPanel>
      <form onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.fields}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                type="email"
                autoComplete="email"
                label={translate(LangEnum.LOGIN_EMAIL)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{ htmlInput: { 'test-id': LoginPageTestIds.LOGIN_EMAIL } }}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState }) => (
              <PasswordField
                {...field}
                autoComplete="current-password"
                label={translate(LangEnum.LOGIN_PASSWORD)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                slotProps={{ htmlInput: { 'test-id': LoginPageTestIds.LOGIN_PASSWORD } }}
              />
            )}
          />
        </div>
        <div className={classes.actions}>
          <Button
            testId={LoginPageTestIds.ENTER_BUTTON}
            variant="contained"
            size="large"
            color="primary"
            type="submit"
            loading={isSubmitting}
            fullWidth
          >
            {translate(LangEnum.LOGIN_LOGIN)}
          </Button>
          <Button
            testId={LoginPageTestIds.OAUTH_BACK_TO_EMAIL_BUTTON}
            variant="text"
            color="secondary"
            size="large"
            fullWidth
            onClick={onBackToEmail}
          >
            {translate(LangEnum.GLOBAL_GO_BACK)}
          </Button>
        </div>
      </form>
    </AuthPanel>
  );
};

export default OAuthPasswordPanel;
